import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
// FP3-035F Issue 2 — Preserve ?as_provider= on student-row navigation.
import useShadowNavigate from "@/lib/useShadowNavigate";
import {
  ArrowLeft,
  ArrowRight,
  CalendarBlank,
  TrendUp,
  TrendDown,
  Minus,
  Target,
  ChartLine,
} from "@phosphor-icons/react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkline } from "./Sparkline";
import { TREND_META, fmtDate, daysSince } from "./utils";
import { formatCanonicalPercent } from "@/lib/goalAverage";
import api from "@/lib/api";

function TrendIcon({ status }) {
  if (status === "improving") return <TrendUp size={14} weight="bold" className="text-[#5A8A6A]" />;
  if (status === "declining") return <TrendDown size={14} weight="bold" className="text-[#B85C5C]" />;
  return <Minus size={14} weight="bold" className="text-[#9B9890]" />;
}

function GoalProgressCard({ goal, onSelect }) {
  const series = goal.trial_runs || [];
  const trendKey = goal.computed_trend?.status || "no_data";
  const meta = TREND_META[trendKey] || TREND_META.no_data;
  const accuracies = series.map((s) => s.accuracy);
  // FP3-130A — Canonical Goal Average (mean of per-session
  // percentages, computed backend-side) replaces the stale
  // `goal.current_mastery` snapshot on the Student Progress card.
  const avg = goal.canonical_average;
  const hasAvg = avg !== null && avg !== undefined;
  const nowDisplay = formatCanonicalPercent(avg);
  const target = goal.target_mastery || 80;
  const baseAcc = parseInt((goal.baseline || "").match(/(\d+)\s*%/)?.[1] || "0");
  const progressPct = hasAvg
    ? Math.min(100, Math.max(0, (Number(avg) / target) * 100))
    : 0;

  return (
    <Card
      data-testid={`goal-card-${goal.id}`}
      className="rounded-xl border border-[#E5E3DB] bg-white shadow-sm hover:shadow-md hover:border-[#8FA998]/40 transition-all cursor-pointer group"
      onClick={() => onSelect(goal.id)}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <Badge variant="outline" className="text-[10px] capitalize border-[#E5E3DB] text-[#6B7280] px-1.5 py-0">
                {goal.category}
              </Badge>
              <Badge
                className="text-[10px] border-0 font-medium px-2 py-0 flex items-center gap-1"
                style={{ backgroundColor: meta.bg, color: meta.text }}
              >
                <TrendIcon status={trendKey} />
                {meta.label}
              </Badge>
              {goal.status === "achieved" && (
                <Badge className="text-[10px] bg-[#EDF2F7] text-[#3D5F82] border-0 px-2 py-0">Achieved</Badge>
              )}
            </div>
            <p className="text-sm text-[#1F2937] line-clamp-2 leading-snug">{goal.goal_text}</p>
            <div className="flex items-center gap-4 mt-3 text-[11px] text-[#6B7280]">
              <span>
                <span className="text-[#9B9890]">Base </span>
                <span className="text-[#1F2937] font-medium">{baseAcc || "—"}%</span>
              </span>
              <ArrowRight size={10} className="text-[#9B9890]" />
              <span>
                <span className="text-[#9B9890]">Avg </span>
                <span
                  className="text-[#1F2937] font-semibold"
                  data-testid={`goal-card-avg-${goal.id}`}
                  title={
                    hasAvg
                      ? `Average across ${goal.canonical_session_count || 0} targeted session${(goal.canonical_session_count || 0) === 1 ? "" : "s"}`
                      : "No data yet"
                  }
                >
                  {nowDisplay}
                </span>
              </span>
              <span>
                <span className="text-[#9B9890]">Target </span>
                <span className="text-[#1F2937] font-medium">{target}%</span>
              </span>
              <span className="ml-auto text-[10px]">
                {goal.computed_trend?.data_points || 0} data point{goal.computed_trend?.data_points !== 1 ? "s" : ""}
              </span>
            </div>
            {/* Progress bar (canonical average vs target) */}
            <div className="mt-2 h-1.5 bg-[#F3F1EC] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${progressPct}%`,
                  backgroundColor: hasAvg && Number(avg) >= target ? "#5A8A6A" : meta.dot,
                }}
              />
            </div>
          </div>
          {/* Sparkline */}
          <div className="shrink-0">
            <Sparkline values={accuracies} width={120} height={48} stroke={meta.dot} fill={meta.dot + "22"} />
            {series.length > 0 && (
              <p className="text-[9px] text-[#9B9890] text-right mt-0.5">
                last {fmtDate(series[series.length - 1]?.date)}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function StudentProgress({ studentId, onBack, onSelectGoal }) {
  const navigate = useShadowNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active");
  const [windowDays, setWindowDays] = useState(60);

  useEffect(() => {
    if (!studentId) return;
    setLoading(true);
    api
      .getStudentProgress(studentId, { days: windowDays })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [studentId, windowDays]);

  if (loading || !data) {
    return (
      <div className="space-y-3" data-testid="student-progress-loading">
        <Skeleton className="h-12 w-64" />
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  }

  const { student, goals, recent_sessions } = data;
  const filteredGoals = goals.filter((g) => {
    if (filter === "active") return g.status === "active";
    if (filter === "achieved") return g.status === "achieved";
    return true;
  });

  return (
    <div data-testid="student-progress">
      {/* Header — FP3-112F: title block up top, "Open Workspace"
          drops to its own row directly below so page-level actions
          never float at the far-right edge of the shell. */}
      <div className="mb-5">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            data-testid="progress-back"
            className="w-8 h-8 rounded-lg border border-[#E5E3DB] flex items-center justify-center hover:bg-[#F3F1EC]"
          >
            <ArrowLeft size={14} className="text-[#6B7280]" />
          </button>
          <div>
            <h2 className="text-lg font-semibold text-[#1F2937] heading-font" data-testid="progress-student-name">
              {student.first_name} {student.last_name}
            </h2>
            <p className="text-xs text-[#6B7280]">
              Gr {student.grade} &middot;{" "}
              {(student.services && student.services.length > 0
                ? student.services.map((s) => s.label || s.key)
                : (student.service_areas || [])
              ).join(", ")}
            </p>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="student-progress-header-actions">
          <button
            onClick={() => navigate(`/students/${student.id}`)}
            className="text-xs text-[#1E3F33] font-medium hover:underline"
            data-testid="open-workspace"
          >
            Open Workspace <ArrowRight size={11} className="inline ml-0.5" />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex bg-[#F3F1EC] rounded-lg p-0.5">
          {[
            { k: "active", label: "Active" },
            { k: "achieved", label: "Achieved" },
            { k: "all", label: "All" },
          ].map((b) => (
            <button
              key={b.k}
              data-testid={`filter-${b.k}`}
              onClick={() => setFilter(b.k)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                filter === b.k ? "bg-white text-[#1E3F33] shadow-sm" : "text-[#6B7280]"
              }`}
            >
              {b.label}
            </button>
          ))}
        </div>
        <div className="flex bg-[#F3F1EC] rounded-lg p-0.5">
          {[
            { k: 30, label: "30d" },
            { k: 60, label: "60d" },
            { k: 90, label: "90d" },
          ].map((b) => (
            <button
              key={b.k}
              data-testid={`window-${b.k}`}
              onClick={() => setWindowDays(b.k)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                windowDays === b.k ? "bg-white text-[#1E3F33] shadow-sm" : "text-[#6B7280]"
              }`}
            >
              {b.label}
            </button>
          ))}
        </div>
        <span className="text-[11px] text-[#6B7280] ml-auto">
          {filteredGoals.length} goal{filteredGoals.length !== 1 ? "s" : ""} &middot;{" "}
          {recent_sessions.length} session{recent_sessions.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Goals */}
      {filteredGoals.length === 0 ? (
        <div className="text-center py-12 text-sm text-[#6B7280]">No goals to show.</div>
      ) : (
        <div className="space-y-2.5">
          {filteredGoals.map((g) => (
            <GoalProgressCard key={g.id} goal={g} onSelect={onSelectGoal} />
          ))}
        </div>
      )}

      {/* Recent sessions */}
      {recent_sessions.length > 0 && (
        <div className="mt-8">
          <h3 className="text-sm font-semibold text-[#1F2937] heading-font mb-2">Recent Sessions</h3>
          <div className="space-y-1.5">
            {recent_sessions.slice(0, 8).map((s) => (
              <div
                key={s.id}
                className="flex items-center gap-3 p-2.5 rounded-lg bg-white border border-[#E5E3DB] text-xs"
                data-testid={`recent-session-${s.id}`}
              >
                <CalendarBlank size={12} className="text-[#6B7280] shrink-0" />
                <span className="text-[#1F2937] font-medium">{fmtDate(s.date)}</span>
                <span className="text-[#6B7280]">{s.start_time?.slice(0, 5)}</span>
                <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-[#E5E3DB] text-[#6B7280]">
                  {s.session_type}
                </Badge>
                {s.status && (
                  <Badge
                    className={`text-[9px] px-1.5 py-0 border-0 ${
                      s.status === "completed"
                        ? "bg-[#E4EFE7] text-[#4A6B52]"
                        : s.status === "scheduled"
                        ? "bg-[#EDF2F7] text-[#3D5F82]"
                        : "bg-[#F3F1EC] text-[#6B7280]"
                    }`}
                  >
                    {s.status}
                  </Badge>
                )}
                {s.notes && <span className="text-[#6B7280] truncate ml-2">{s.notes}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
